import { inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useBreakpoint } from 'src/composables/useBreakpoint';
import { useFilterStore } from 'src/stores/filter';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { useIdoListStore } from 'src/stores/idoList';
import { useSearchHistoryStore } from 'src/stores/searchHistory';
import { useUserInterfaceSettingsStore } from 'src/stores/userInterfaceSettings';
import { useWorkspaceTabsStore } from 'src/stores/workspaceTabs';

import { EventBus } from 'src/util/eventBus/EventBus';
import { clearIdoGlobalSearch, executeIdoGlobalSearch } from 'src/util/ido/idoGlobalSearch';
import { canUseWorkspaceTabs } from 'src/util/platform/browserInteractionCapabilities';
import { QueryStore } from 'src/util/query/QueryStore';

export interface ModuleIcon {
	iconLight: string;
	iconSolid: string;
	label: string;
}

interface SearchExecutionState {
	clearSearch: (module: string) => void;
	executeSearch: (module: string, term: string) => Promise<void>;
	getSearchTermFromStore: (module: string) => string;
	moduleIcons: ModuleIcon[];
	moduleMap: string[];
	setSearchTermInStore: (module: string, term: string) => void;
}

const EVENT_MARKERS = ['FAVORITES_ACTIVE', 'FAVORITE_TOGGLE'];

export const MODULE_MAP = [
	'inbox',
	'records',
	'phoneCallList',
	'taskList',
	'documentList',
	'mailList',
	'messageList',
	'noteList',
	'agendaList',
	'trashList',
];

export function useSearchExecution(): SearchExecutionState {
	const filterStore = useFilterStore();
	const genericActivityListStore = useGenericActivityListStore();
	const idoListStore = useIdoListStore();
	const searchHistoryStore = useSearchHistoryStore();
	const userInterfaceSettingsStore = useUserInterfaceSettingsStore();
	const workspaceTabsStore = useWorkspaceTabsStore();
	const router = useRouter();
	const breakpoint = useBreakpoint();
	const { t } = useI18n();
	const eventBus = inject('eventBus') as EventBus;
	const queryStore = inject('queryStore') as QueryStore;

	const isWorkspaceRecordsSearch = (): boolean =>
		canUseWorkspaceTabs(
			userInterfaceSettingsStore.workspaceTabsEnabled,
			userInterfaceSettingsStore.splitViewEnabled,
			breakpoint.isDesktop.value
		);

	const moduleIcons: ModuleIcon[] = [
		{ iconLight: 'fa-light fa-house', iconSolid: 'fa-solid fa-house', label: t('menu_inbox') || 'Inbox' },
		{
			iconLight: 'fa-light fa-notebook',
			iconSolid: 'fa-solid fa-notebook',
			label: t('menu_idos') || 'Akten',
		},
		{ iconLight: 'fa-light fa-phone', iconSolid: 'fa-solid fa-phone', label: t('menu_phonecalls') || 'Anrufe' },
		{
			iconLight: 'fa-light fa-square-check',
			iconSolid: 'fa-solid fa-square-check',
			label: t('menu_tasks') || 'Aufgaben',
		},
		{
			iconLight: 'fa-light fa-file-lines',
			iconSolid: 'fa-solid fa-file-lines',
			label: t('menu_documents') || 'Dokumente',
		},
		{
			iconLight: 'fa-light fa-envelope',
			iconSolid: 'fa-solid fa-envelope',
			label: t('menu_mails') || 'E-Mails',
		},
		{
			iconLight: 'fa-light fa-comment-alt',
			iconSolid: 'fa-solid fa-comment-alt',
			label: t('menu_messages') || 'Nachrichten',
		},
		{
			iconLight: 'fa-light fa-pen-to-square',
			iconSolid: 'fa-solid fa-pen-to-square',
			label: t('menu_notes') || 'Merkzettel',
		},
		{
			iconLight: 'fa-light fa-calendar-days',
			iconSolid: 'fa-solid fa-calendar',
			label: t('menu_appointments') || 'Termine',
		},
		{ iconLight: 'fa-light fa-trash', iconSolid: 'fa-solid fa-trash', label: t('menu_trash') || 'Papierkorb' },
	];

	const isEventSystemMarker = (term: string): boolean => EVENT_MARKERS.includes(term);

	const getSearchTermFromStore = (module: string): string => {
		let storedTerm = '';

		if (module === 'records') {
			storedTerm = idoListStore.globalSearchTerm;
		} else {
			const getter: unknown = filterStore.searchTerm(module);
			storedTerm = typeof getter === 'string' ? getter : '';
		}

		const cleanStoredTerm = isEventSystemMarker(storedTerm) ? '' : storedTerm;

		if (cleanStoredTerm && module !== 'records') {
			const activeFavorites = searchHistoryStore.getActiveFavoriteForModule(module);
			const isPureFavorite = activeFavorites.some((fav: { query: string }) => fav.query === cleanStoredTerm);

			if (isPureFavorite) {
				const hiddenSearchTerm = filterStore.hiddenSearchTerm(module);
				if (
					hiddenSearchTerm &&
					hiddenSearchTerm.includes(cleanStoredTerm) &&
					hiddenSearchTerm !== cleanStoredTerm
				) {
					return hiddenSearchTerm;
				}
			}
		}

		return cleanStoredTerm;
	};

	const setSearchTermInStore = (module: string, term: string): void => {
		if (isEventSystemMarker(term)) {
			return;
		}

		if (module === 'records') {
			idoListStore.setGlobalSearchTerm(term);
		} else {
			eventBus.emit('clearTheScrollPosAndGrouping');
			eventBus.emit('recalculateTheGroupingHeader');

			const activeFavorites = searchHistoryStore.getActiveFavoriteForModule(module);

			if (activeFavorites.length > 0) {
				void filterStore.setHiddenSearchTerm({ hiddenSearchTerm: term, module });
				void filterStore.setSearchTerm({ module, searchTerm: term });
			} else {
				void filterStore.setHiddenSearchTerm({ hiddenSearchTerm: '', module });
				void filterStore.setSearchTerm({ module, searchTerm: term });
			}
		}
	};

	const executeSearch = async (module: string, term: string): Promise<void> => {
		const trimmedTerm = term.trim();

		if (trimmedTerm === 'FAVORITE_TOGGLE') {
			eventBus.emit('favoriteToggle', module);
			return;
		}

		setSearchTermInStore(module, trimmedTerm);

		searchHistoryStore.addSearchEntry({ module, query: trimmedTerm });

		if (module === 'records') {
			if (isWorkspaceRecordsSearch()) {
				await executeIdoGlobalSearch({
					eventBus,
					idoListStore,
					queryStore,
					router,
					searchTerm: trimmedTerm,
					workspaceTabsStore,
				});
			} else {
				idoListStore.setGlobalSearchTerm(trimmedTerm);

				if (trimmedTerm) {
					await router.push({ name: 'records', query: { globalSearch: trimmedTerm } });
				} else {
					await router.push({ name: 'records' });
				}
			}
		} else {
			const currentDate = new Date().toISOString().split('T')[0];

			void genericActivityListStore.setActiveView({
				activeView: module === 'inbox' ? 'open' : 'all',
				genericListIdentifier: module,
			});

			if (trimmedTerm) {
				await router.push({
					name: module,
					params: {
						date: currentDate,
						source: 'all',
						view: module === 'inbox' ? 'open' : 'all',
					},
					query: { search: trimmedTerm },
				});
			} else {
				await router.push({
					name: module,
					params: {
						date: currentDate,
						source: 'all',
						view: 'all',
					},
				});
			}
		}
	};

	const clearSearch = (module: string): void => {
		setSearchTermInStore(module, '');

		if (module !== 'records') {
			void filterStore.setHiddenSearchTerm({ hiddenSearchTerm: '', module });
		}

		if (module === 'records') {
			if (isWorkspaceRecordsSearch()) {
				void clearIdoGlobalSearch({ idoListStore, queryStore });
			} else {
				idoListStore.setGlobalSearchTerm('');
				idoListStore.clearGlobalSearchResults();
				idoListStore.setGlobalSearchExplicitlyCleared(true);
				void idoListStore.fetchIdosInInstanceCountList({ queryStore });
				void router.push({ name: 'records' });
			}
		} else if (module === 'agendaList') {
			void router.push({ name: 'agendaList' });
		} else {
			const currentDate = new Date().toISOString().split('T')[0];
			void router.push({
				name: module,
				params: {
					date: currentDate,
					source: 'all',
					view: module === 'inbox' ? 'open' : 'all',
				},
			});
		}
	};

	return {
		clearSearch,
		executeSearch,
		getSearchTermFromStore,
		moduleIcons,
		moduleMap: MODULE_MAP,
		setSearchTermInStore,
	};
}
